import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';

import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import Collapse from '@material-ui/core/Collapse';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';
import AddIcon from '@material-ui/icons/Add';
import SettingsIcon from '@material-ui/icons/Settings';
import VisibilityIcon from '@material-ui/icons/Visibility';
import GetAppIcon from '@material-ui/icons/GetApp';
import ArchiveIcon from '@material-ui/icons/Archive';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import RestoreFromTrash from '@material-ui/icons/RestoreFromTrash';
import QueueIcon from '@material-ui/icons/Queue';
import PhotoLibraryIcon from '@material-ui/icons/PhotoLibrary';
import CollectionsBookmarkIcon from '@material-ui/icons/CollectionsBookmark';
import SettingsBackupRestoreIcon from '@material-ui/icons/SettingsBackupRestore';
import FullscreenIcon from '@material-ui/icons/Fullscreen';

import Loader from '../../../../components/Loader';
import EnhancedTable from '../../components/Table';
import PageHeader from '../../components/PageHeader';
import FilterBar from '../../components/FilterBar';
import DeleteDialog from '../../../../components/DeleteDialog';
import AiImageDownloadDialog from '../../components/AiImageDownloadDialog';
import ContentEditor from '../../components/ContentEditor';
import { useQuery } from '../../../../constants';
import {
  fetchAllAiRuns,
  createAiRun,
  fetchAiSettings,
  updateAiSettings,
  resetAiSystemPromptToDefault,
  getAiImageUrl,
  archiveAiRun,
  restoreAiRun,
  deleteAiRun,
  upscaleAiImage,
  checkUpscaleStatus,
} from '../../redux/actions/ai';
import MediaSettingsFields from './MediaSettingsFields';
import {
  optionValues,
  getOptionsForModel,
  DEFAULT_AVAILABLE_MODELS,
  DEFAULT_IMAGE_MODELS,
  DEFAULT_VIDEO_MODELS,
  clampVideoDuration,
  pickListedModel,
} from './helpers';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  settingsPanel: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    background: '#fafafa',
    border: '1px solid #e0e0e0',
  },
  settingsActions: {
    marginTop: theme.spacing(1),
    display: 'flex',
    '& > *:not(:first-child)': {
      marginLeft: theme.spacing(1),
    },
  },
  promptHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing(1),
  },
  promptEditor: {
    marginBottom: theme.spacing(1),
  },
  fullPromptEditor: {
    minHeight: '70vh',
  },
  promptCell: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
    display: '-webkit-box',
    minWidth: 360,
    maxWidth: 520,
    overflow: 'hidden',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 3,
    whiteSpace: 'normal',
  },
  promptTooltip: {
    maxWidth: 560,
    fontSize: 13,
    lineHeight: 1.45,
    whiteSpace: 'pre-wrap',
  },
}));

export default function AiRuns({ name }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const isArchivedQuery = useQuery('isArchived') === 'true';
  const { runs, fetching, actionLoading, settings, drafts, emptyRuns } = useSelector(({ admin }) => admin.ai);

  const [sort, setSort] = useState({ orderBy: 'createdAt', orderDir: 'desc' });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [modelName, setModelName] = useState('gpt-image-2');
  const [resolution, setResolution] = useState('1k');
  const [aspectRatio, setAspectRatio] = useState('1:1');
  const [videoModelName, setVideoModelName] = useState('seedance-2.5');
  const [videoResolution, setVideoResolution] = useState('720p');
  const [videoAspectRatio, setVideoAspectRatio] = useState('16:9');
  const [videoDuration, setVideoDuration] = useState(5);
  const [promptDialog, setPromptDialog] = useState({ open: false, text: '' });
  const [promptEditorOpen, setPromptEditorOpen] = useState(false);
  const [downloadSelection, setDownloadSelection] = useState(null);

  const [view, setView] = useState(isArchivedQuery ? 'archived' : 'normal');
  const [searchString, setSearchString] = useState('');

  const [archiveId, setArchiveId] = useState(null);
  const [openArchiveDialog, setOpenArchiveDialog] = useState(false);

  const [restoreId, setRestoreId] = useState(null);
  const [openRestoreDialog, setOpenRestoreDialog] = useState(false);

  const [deleteId, setDeleteId] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const availableModels = settings?.availableModels?.length > 0 ? settings.availableModels : DEFAULT_AVAILABLE_MODELS;
  const imageModels = settings?.availableImageModels?.length > 0 ? settings.availableImageModels : DEFAULT_IMAGE_MODELS;
  const videoModels = settings?.availableVideoModels?.length > 0 ? settings.availableVideoModels : DEFAULT_VIDEO_MODELS;

  useEffect(() => {
    dispatch(fetchAllAiRuns({ orderBy: 'createdAt', orderDir: 'desc', isArchived: isArchivedQuery }));
    dispatch(fetchAiSettings());
    if (isArchivedQuery) {
      setView('archived');
    }
  }, [dispatch, isArchivedQuery]);

  useEffect(() => {
    if (settings) {
      setSystemPrompt(settings.aiSystemPrompt || '');
      const nextImageModels = settings.availableImageModels?.length > 0 ? settings.availableImageModels : DEFAULT_IMAGE_MODELS;
      const nextModelName = pickListedModel(nextImageModels, settings.aiModelName, 'gpt-image-2');
      const nextOptions = getOptionsForModel(nextModelName);
      setModelName(nextModelName);
      setResolution(
        optionValues(nextOptions.resolutions).includes(settings.aiResolution)
          ? settings.aiResolution
          : nextOptions.defaultResolution,
      );
      setAspectRatio(
        optionValues(nextOptions.aspectRatios).includes(settings.aiAspectRatio)
          ? settings.aiAspectRatio
          : nextOptions.defaultAspectRatio,
      );
      const nextVideoModels = settings.availableVideoModels?.length > 0 ? settings.availableVideoModels : DEFAULT_VIDEO_MODELS;
      const nextVideoModel = pickListedModel(nextVideoModels, settings.aiVideoModelName, 'seedance-2.5');
      const nextVideoOptions = getOptionsForModel(nextVideoModel);
      setVideoModelName(nextVideoModel);
      setVideoResolution(
        optionValues(nextVideoOptions.resolutions).includes(settings.aiVideoResolution)
          ? settings.aiVideoResolution
          : nextVideoOptions.defaultResolution,
      );
      setVideoAspectRatio(
        optionValues(nextVideoOptions.aspectRatios).includes(settings.aiVideoAspectRatio)
          ? settings.aiVideoAspectRatio
          : nextVideoOptions.defaultAspectRatio,
      );
      setVideoDuration(clampVideoDuration(settings.aiVideoDuration));
    }
  }, [settings]);

  const handleModelChange = (nextModelName) => {
    const nextOptions = getOptionsForModel(nextModelName);
    setModelName(nextModelName);
    setResolution((current) =>
      optionValues(nextOptions.resolutions).includes(current) ? current : nextOptions.defaultResolution,
    );
    setAspectRatio((current) =>
      optionValues(nextOptions.aspectRatios).includes(current) ? current : nextOptions.defaultAspectRatio,
    );
  };

  const handleVideoModelChange = (nextModelName) => {
    const nextOptions = getOptionsForModel(nextModelName);
    setVideoModelName(nextModelName);
    setVideoResolution((current) =>
      optionValues(nextOptions.resolutions).includes(current) ? current : nextOptions.defaultResolution,
    );
    setVideoAspectRatio((current) =>
      optionValues(nextOptions.aspectRatios).includes(current) ? current : nextOptions.defaultAspectRatio,
    );
  };

  const loadPage = (page, sortOptions = {}) => {
    dispatch(
      fetchAllAiRuns({
        page,
        limit: runs?.limit || 25,
        ...sort,
        ...sortOptions,
        isArchived: view === 'archived',
        search: searchString,
      }),
    );
    if (sortOptions.orderBy) setSort(sortOptions);
  };

  const handleSearch = (search) => {
    setSearchString(search);
    dispatch(
      fetchAllAiRuns({
        search,
        isArchived: view === 'archived',
        ...sort,
      }),
    );
  };

  const handleFolderToggle = () => {
    setSearchString('');
    let options = {
      isArchived: view === 'normal',
      ...sort,
    };
    dispatch(fetchAllAiRuns(options));
    setView(view === 'normal' ? 'archived' : 'normal');
  };

  const handleNewRun = async () => {
    const run = await dispatch(createAiRun());
    if (run?._id) {
      history.push(`/superadmin/ai/runs/${run._id}`);
    }
  };

  const handleSaveSettings = () => {
    dispatch(
      updateAiSettings({
        aiSystemPrompt: systemPrompt,
        aiModelName: modelName,
        aiResolution: resolution,
        aiAspectRatio: aspectRatio,
        aiVideoModelName: videoModelName,
        aiVideoResolution: videoResolution,
        aiVideoAspectRatio: videoAspectRatio,
        aiVideoDuration: videoDuration,
      }),
    );
  };

  const handleResetSystemPrompt = () => {
    dispatch(resetAiSystemPromptToDefault());
  };

  const modelLabel = (id) => {
    const found = availableModels.find((m) => m.id === id);
    return found?.label || id || '—';
  };

  const openImage = async (run, download = false) => {
    const messages = run.messages || [];
    let index = -1;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].imageKey || messages[i].videoKey) {
        index = i;
        break;
      }
    }
    if (index < 0) return;
    if (download && messages[index].imageKey) {
      setDownloadSelection({ runId: run._id, index, imageUrl: messages[index].imageUrl });
      return;
    }
    try {
      const url =
        !download && run.lastImageUrl && messages[index].imageKey
          ? run.lastImageUrl
          : await getAiImageUrl(run._id, index, download);
      if (url) {
        if (download) window.location.assign(url);
        else window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (e) {
      // snackbar handled by caller where applicable
    }
  };

  const combinedRuns = useMemo(() => {
    if (!runs || view === 'archived') return runs; // Only show drafts in normal view
    const backendDocs = [...(runs.docs || [])];
    const draftDocs = [];

    // Inject drafts that are not in the backend list
    Object.keys(drafts || {}).forEach((id) => {
      const prompt = drafts[id];
      if (prompt && prompt.trim()) {
        const backendIndex = backendDocs.findIndex((r) => r._id === id);
        if (backendIndex >= 0) {
          // It's already in the list, just mark it as draft and use draft prompt
          backendDocs[backendIndex] = {
            ...backendDocs[backendIndex],
            isDraft: true,
            draftPrompt: prompt,
          };
        } else if (emptyRuns && emptyRuns[id]) {
          // It's a new empty run with a draft
          draftDocs.push({
            ...emptyRuns[id],
            isDraft: true,
            draftPrompt: prompt,
          });
        }
      }
    });

    // Sort draftDocs by createdAt desc
    draftDocs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    return { ...runs, docs: [...draftDocs, ...backendDocs] };
  }, [runs, drafts, emptyRuns, view]);

  const headCells = [
    {
      id: 'image',
      label: 'Image',
      disableSorting: true,
      formatter: (row) =>
        row.lastImageThumbnailUrl ? (
          // eslint-disable-next-line jsx-a11y/img-redundant-alt
          <img
            src={row.lastImageThumbnailUrl || row.lastImageUrl}
            alt="Latest generated image"
            className="zoomable-thumbnail"
            style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4, border: '1px solid #eee' }}
            onError={(e) => {
              if (row.lastImageUrl && e.target.src !== row.lastImageUrl) {
                e.target.src = row.lastImageUrl;
              } else {
                e.target.style.display = 'none';
              }
            }}
          />
        ) : null,
    },
    {
      id: 'createdAt',
      label: 'Created',
      formatter: (row) => (row.createdAt ? dayjs(row.createdAt).format('MMM D, YYYY h:mm A') : '—'),
    },
    {
      id: 'lastPrompt',
      label: 'Prompt',
      formatter: (row) => {
        const promptText = row.isDraft ? row.draftPrompt : row.lastPrompt || '';
        return (
          <Tooltip title={promptText || '—'} placement="top-start" classes={{ tooltip: classes.promptTooltip }}>
            <span className={classes.promptCell}>
              {row.isDraft && <span style={{ color: '#f57c00', fontWeight: 500, marginRight: 4 }}>[Draft]</span>}
              {promptText}
            </span>
          </Tooltip>
        );
      },
    },
    {
      id: 'modelName',
      label: 'Model',
      formatter: (row) => modelLabel(row.modelName),
    },
    {
      id: 'actions',
      label: 'Actions',
      actions:
        view === 'normal'
          ? [
              {
                label: 'Open run',
                action: (r) => history.push(`/superadmin/ai/runs/${r._id}`),
                icon: <VisibilityIcon />,
                disabled: () => fetching || actionLoading,
              },
              {
                label: 'View media',
                action: (r) => openImage(r, false),
                icon: <VisibilityIcon fontSize="small" />,
                disabled: (r) =>
                  !(r.lastImageKey || r.messages?.some((m) => m.imageKey || m.videoKey)) || fetching || actionLoading,
              },
              {
                label: 'Download',
                action: (r) => openImage(r, true),
                icon: <GetAppIcon />,
                disabled: (r) =>
                  !(r.lastImageKey || r.messages?.some((m) => m.imageKey || m.videoKey)) || fetching || actionLoading,
              },
              {
                label: 'Archive',
                action: (r) => {
                  setArchiveId(r._id);
                  setOpenArchiveDialog(true);
                },
                icon: <ArchiveIcon />,
                disabled: () => fetching || actionLoading,
              },
            ]
          : [
              {
                label: 'Restore',
                action: (r) => {
                  setRestoreId(r._id);
                  setOpenRestoreDialog(true);
                },
                icon: <RestoreFromTrash />,
                disabled: () => fetching || actionLoading,
              },
              {
                label: 'Permanently Delete',
                action: (r) => {
                  setDeleteId(r._id);
                  setOpenDeleteDialog(true);
                },
                icon: <DeleteForeverIcon />,
                disabled: () => fetching || actionLoading,
              },
            ],
    },
  ];

  return (
    <div className={classes.root}>
      {(fetching || actionLoading) && <Loader fetching={fetching || actionLoading} />}
      <PageHeader
        disableSearch
        title={name}
        options={
          <>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<CollectionsBookmarkIcon />}
              onClick={() => history.push('/superadmin/ai/assets')}
              style={{ marginRight: 8 }}
            >
              Native Assets
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<PhotoLibraryIcon />}
              onClick={() => history.push('/superadmin/ai/library')}
              style={{ marginRight: 8 }}
            >
              AI Assets
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<QueueIcon />}
              onClick={() => history.push('/superadmin/ai/gif-queue')}
              style={{ marginRight: 8 }}
            >
              GIF Queue
            </Button>
            <IconButton title="System prompt settings" onClick={() => setSettingsOpen((v) => !v)}>
              <SettingsIcon />
            </IconButton>
            <IconButton title="New run" onClick={handleNewRun}>
              <AddIcon />
            </IconButton>
          </>
        }
      />

      <FilterBar
        toggle={view === 'archived'}
        searchValue={searchString}
        onSearch={handleSearch}
        onAction={handleFolderToggle}
        enableTopics={false}
      />

      <Collapse in={settingsOpen}>
        <div className={classes.settingsPanel}>
          <Typography variant="subtitle1" gutterBottom>
            AI settings
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Image and video defaults are saved on the general settings document and copied when a new run is created.
          </Typography>
          <MediaSettingsFields
            imageModels={imageModels}
            imageModel={modelName}
            imageResolution={resolution}
            imageAspectRatio={aspectRatio}
            onImageModelChange={handleModelChange}
            onImageResolutionChange={setResolution}
            onImageAspectRatioChange={setAspectRatio}
            videoModels={videoModels}
            videoModel={videoModelName}
            videoResolution={videoResolution}
            videoAspectRatio={videoAspectRatio}
            videoDuration={videoDuration}
            onVideoModelChange={handleVideoModelChange}
            onVideoResolutionChange={setVideoResolution}
            onVideoAspectRatioChange={setVideoAspectRatio}
            onVideoDurationChange={setVideoDuration}
            disabled={actionLoading}
          />
          <div className={classes.promptHeader}>
            <Typography variant="subtitle2">System prompt</Typography>
            <IconButton
              title="Open full editor"
              aria-label="Open full editor"
              onClick={() => setPromptEditorOpen(true)}
              size="small"
            >
              <FullscreenIcon />
            </IconButton>
          </div>
          <div className={classes.promptEditor}>
            <ContentEditor
              markdown
              id="ai-system-prompt"
              value={systemPrompt}
              handleEditorChange={setSystemPrompt}
              config={{ height: 220 }}
            />
          </div>
          <div className={classes.settingsActions}>
            <Button color="primary" variant="contained" onClick={handleSaveSettings} disabled={actionLoading}>
              Save
            </Button>
            <Button
              color="primary"
              variant="outlined"
              startIcon={<SettingsBackupRestoreIcon />}
              onClick={handleResetSystemPrompt}
              disabled={actionLoading}
            >
              Set to default
            </Button>
            <Button onClick={() => setSettingsOpen(false)}>Close</Button>
          </div>
        </div>
      </Collapse>

      {combinedRuns && combinedRuns.docs && combinedRuns.docs.length > 0 ? (
        <Grid container direction="row" justifyContent="flex-start" spacing={2}>
          <Grid item sm={12} xs={12}>
            <EnhancedTable
              data={combinedRuns}
              headCells={headCells}
              handlePage={loadPage}
              handleClick={(row) => {
                if (fetching || actionLoading) return;
                history.push(`/superadmin/ai/runs/${row._id}`);
              }}
              orderField={sort.orderBy}
              orderDirection={sort.orderDir}
              handleOrder={(d) => loadPage(combinedRuns.page, d)}
            />
          </Grid>
        </Grid>
      ) : (
        !fetching && (
          <Typography color="textSecondary" style={{ marginTop: 16 }}>
            No AI runs yet.{' '}
            {view === 'normal' && (
              <Link component="button" onClick={handleNewRun}>
                Create a new run
              </Link>
            )}
          </Typography>
        )
      )}

      <Dialog open={promptEditorOpen} onClose={() => setPromptEditorOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>System prompt</DialogTitle>
        <DialogContent>
          <div className={classes.fullPromptEditor}>
            <ContentEditor
              markdown
              id="ai-system-prompt-full"
              value={systemPrompt}
              handleEditorChange={setSystemPrompt}
              config={{ height: '70vh', max_height: 800 }}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={() => setPromptEditorOpen(false)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={promptDialog.open}
        onClose={() => setPromptDialog({ open: false, text: '' })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Full prompt</DialogTitle>
        <DialogContent>
          <Typography style={{ whiteSpace: 'pre-wrap' }}>{promptDialog.text || '—'}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromptDialog({ open: false, text: '' })}>Close</Button>
        </DialogActions>
      </Dialog>

      <AiImageDownloadDialog
        open={Boolean(downloadSelection)}
        imageUrl={downloadSelection?.imageUrl || ''}
        onClose={() => setDownloadSelection(null)}
        onUpscale={
          downloadSelection
            ? () => ({
                initiate: () => upscaleAiImage(downloadSelection.runId, downloadSelection.index),
                checkStatus: () => checkUpscaleStatus(downloadSelection.runId, downloadSelection.index),
              })
            : null
        }
        onDownloadOriginal={
          downloadSelection ? () => getAiImageUrl(downloadSelection.runId, downloadSelection.index, true) : null
        }
      />

      <DeleteDialog
        open={openArchiveDialog}
        content={'Are you sure you want to archive this AI run?'}
        cancelLabel={'No'}
        confirmLabel={'Yes'}
        handleClose={() => setOpenArchiveDialog(false)}
        handleConfirm={() => {
          dispatch(archiveAiRun(archiveId));
          setArchiveId(null);
          setOpenArchiveDialog(false);
        }}
      />

      <DeleteDialog
        open={openRestoreDialog}
        content={'Are you sure you want to restore this AI run?'}
        cancelLabel={'No'}
        confirmLabel={'Yes'}
        handleClose={() => setOpenRestoreDialog(false)}
        handleConfirm={() => {
          dispatch(restoreAiRun(restoreId));
          setRestoreId(null);
          setOpenRestoreDialog(false);
        }}
      />

      <DeleteDialog
        open={openDeleteDialog}
        content={'Warning! This will permanently delete the AI run and its messages. Are you sure you want to proceed?'}
        cancelLabel={'No'}
        confirmLabel={'Yes'}
        handleClose={() => setOpenDeleteDialog(false)}
        handleConfirm={() => {
          dispatch(deleteAiRun(deleteId));
          setDeleteId(null);
          setOpenDeleteDialog(false);
        }}
      />
    </div>
  );
}
